import { useMemo, useState } from "react";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useFormules } from "@/store/formules/FormulesProvider";
import { useBilling } from "@/store/billing/BillingProvider";
import {
  EMAIL_VALIDATION_ICONS,
  EMAIL_VALIDATION_LABELS,
  EMAIL_VALIDATION_TONES,
  PAIEMENT_METHODE_LABELS,
  PAIEMENT_METHODE_OPTIONS,
  PAIEMENT_STATUT_LABELS,
  PAIEMENT_STATUT_OPTIONS,
  PAIEMENT_STATUT_TONES,
  paiementReference,
  peutRenvoyerEmailValidation,
  type PaiementStatut,
  type PlatformPaiement,
} from "@/store/billing/types";
import { formatDate, formatFcfa } from "@/utils/format";
import type { ExportColumn } from "@/utils/export";
import ConfirmationModal from "@/pages/super-admin/components/ConfirmationModal";
import PaiementForm from "@/pages/super-admin/components/PaiementForm";
import PreuvePaiementModal from "@/pages/super-admin/components/PreuvePaiementModal";

type SortKey = "recent" | "montant" | "compte";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "montant", label: "Montant (décroissant)" },
  { value: "compte", label: "Compte (A → Z)" },
];

export default function SuperAdminPaiementsPage() {
  const { formules } = useFormules();
  const { paiements, abonnements, chargement, erreur, recharger, removePaiement, traiterPaiement, renvoyerEmailValidation } =
    useBilling();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformPaiement | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PlatformPaiement | null>(null);
  const [pendingStatut, setPendingStatut] = useState<{ paiement: PlatformPaiement; statut: PaiementStatut } | null>(null);
  const [preuvePaiement, setPreuvePaiement] = useState<PlatformPaiement | null>(null);
  const [emailEnCours, setEmailEnCours] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [methodeFilter, setMethodeFilter] = useState("");
  const [formuleFilter, setFormuleFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");

  const formuleNom = (id: string) => formules.find((formule) => formule.id === id)?.nom ?? id;

  const stats = useMemo(
    () => ({
      total: paiements.length,
      montantValide: paiements.filter((item) => item.statut === "valide").reduce((sum, item) => sum + item.montant, 0),
      attente: paiements.filter((item) => item.statut === "en_attente").length,
      refuse: paiements.filter((item) => item.statut === "rejete").length,
    }),
    [paiements]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = paiements.filter((item) => {
      if (statutFilter && item.statut !== statutFilter) return false;
      if (methodeFilter && item.methode !== methodeFilter) return false;
      if (formuleFilter && item.formuleId !== formuleFilter) return false;
      const reference = item.reference || paiementReference(item.id);
      if (term && !`${reference} ${item.compteNom} ${formuleNom(item.formuleId)}`.toLowerCase().includes(term)) return false;
      return true;
    });
    return result.sort((a, b) => {
      if (sort === "montant") return b.montant - a.montant;
      if (sort === "compte") return a.compteNom.localeCompare(b.compteNom, "fr");
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [paiements, search, statutFilter, methodeFilter, formuleFilter, sort]);

  const isFiltering = Boolean(search.trim() || statutFilter || methodeFilter || formuleFilter);

  const exportColumns: ExportColumn<PlatformPaiement>[] = [
    { header: "Référence", value: (item) => item.reference || paiementReference(item.id) },
    { header: "Compte", value: (item) => item.compteNom },
    { header: "Formule", value: (item) => formuleNom(item.formuleId) },
    { header: "Montant", value: (item) => item.montant },
    { header: "Date", value: (item) => formatDate(item.date) },
    { header: "Méthode", value: (item) => PAIEMENT_METHODE_LABELS[item.methode] },
    { header: "Période", value: (item) => item.periode },
    { header: "Statut", value: (item) => PAIEMENT_STATUT_LABELS[item.statut] },
  ];

  const resetFilters = () => {
    setSearch("");
    setStatutFilter("");
    setMethodeFilter("");
    setFormuleFilter("");
  };

  const columns: DataTableColumn<PlatformPaiement>[] = [
    {
      key: "reference",
      header: "Référence",
      className: "min-w-[150px]",
      render: (paiement) => (
        <span className="font-label font-medium text-foreground-950">
          {paiement.reference || paiementReference(paiement.id)}
        </span>
      ),
    },
    {
      key: "compte",
      header: "Compte",
      className: "min-w-[170px]",
      render: (paiement) => <span className="text-foreground-700">{paiement.compteNom}</span>,
    },
    {
      key: "formule",
      header: "Formule",
      className: "min-w-[130px]",
      render: (paiement) => <span className="text-foreground-700">{formuleNom(paiement.formuleId)}</span>,
    },
    {
      key: "montant",
      header: "Montant",
      align: "right",
      className: "min-w-[110px]",
      render: (paiement) => (
        <span className="font-label font-medium text-foreground-950">{formatFcfa(paiement.montant)}</span>
      ),
    },
    {
      key: "methode",
      header: "Méthode",
      className: "min-w-[130px]",
      render: (paiement) => <span className="text-foreground-700">{PAIEMENT_METHODE_LABELS[paiement.methode]}</span>,
    },
    {
      key: "periode",
      header: "Période",
      className: "min-w-[100px]",
      render: (paiement) => <span className="text-foreground-700">{paiement.periode || "—"}</span>,
    },
    {
      key: "date",
      header: "Date",
      className: "min-w-[120px]",
      render: (paiement) => <span className="text-foreground-700">{formatDate(paiement.date)}</span>,
    },
    {
      key: "statut",
      header: "Statut",
      render: (paiement) => (
        <Badge tone={PAIEMENT_STATUT_TONES[paiement.statut]}>{PAIEMENT_STATUT_LABELS[paiement.statut]}</Badge>
      ),
    },
    {
      key: "email",
      header: "E-mail",
      className: "min-w-[130px]",
      render: (paiement) => (
        <span title={paiement.emailErreur || "E-mail de confirmation après validation du paiement"}>
          <Badge tone={EMAIL_VALIDATION_TONES[paiement.emailStatut]} icon={EMAIL_VALIDATION_ICONS[paiement.emailStatut]}>
            {EMAIL_VALIDATION_LABELS[paiement.emailStatut]}
          </Badge>
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[300px]",
      render: (paiement) => (
        <div className="flex items-center justify-end gap-2">
          {paiement.preuveUrl && (
            <button
              type="button"
              onClick={() => setPreuvePaiement(paiement)}
              title="Voir la preuve de paiement"
              aria-label="Voir la preuve de paiement"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-accent-300 bg-accent-50 text-accent-800 transition-colors hover:bg-accent-100 cursor-pointer"
            >
              <i className="ri-file-image-line text-base" aria-hidden="true" />
            </button>
          )}
          {peutRenvoyerEmailValidation(paiement) && (
            <button
              type="button"
              onClick={() => void renvoyerEmail(paiement)}
              disabled={emailEnCours === paiement.id}
              title="Renvoyer l'e-mail de confirmation au client"
              aria-label="Renvoyer l'e-mail de confirmation au client"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-secondary-300 bg-secondary-50 text-secondary-700 transition-colors hover:bg-secondary-100 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            >
              <i
                className={emailEnCours === paiement.id ? "ri-loader-4-line animate-spin text-base" : "ri-mail-send-line text-base"}
                aria-hidden="true"
              />
            </button>
          )}
          {paiement.statut !== "valide" && (
            <button
              type="button"
              onClick={() => setPendingStatut({ paiement, statut: "valide" })}
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-secondary-300 bg-secondary-50 px-3 py-2 text-xs font-label font-medium text-secondary-800 transition-colors hover:bg-secondary-100 cursor-pointer"
            >
              <i className="ri-checkbox-circle-line text-base" aria-hidden="true" />
              Valider
            </button>
          )}
          {paiement.statut !== "rejete" && (
            <button
              type="button"
              onClick={() => setPendingStatut({ paiement, statut: "rejete" })}
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-2 text-xs font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
            >
              <i className="ri-close-circle-line text-base" aria-hidden="true" />
              Refuser
            </button>
          )}
          <CardActions
            onEdit={() => {
              setEditing(paiement);
              setFormOpen(true);
            }}
            onDelete={() => setPendingDelete(paiement)}
          />
        </div>
      ),
    },
  ];

  const confirmDelete = () => {
    if (!pendingDelete) return;
    removePaiement(pendingDelete.id);
    setPendingDelete(null);
  };

  const confirmStatut = () => {
    if (!pendingStatut) return;
    void traiterPaiement(pendingStatut.paiement.id, pendingStatut.statut === "valide");
    setPendingStatut(null);
  };

  const renvoyerEmail = async (paiement: PlatformPaiement) => {
    if (emailEnCours) return;
    setEmailEnCours(paiement.id);
    try {
      await renvoyerEmailValidation(paiement.id);
    } finally {
      setEmailEnCours(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Paiements</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Paiements des abonnements à la plateforme (Orange Money, Moov Money, espèces).
            Chaque paiement est rattaché à un abonnement : consultez la preuve, validez ou refusez un règlement en toute sécurité.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu
            rows={filtered}
            columns={exportColumns}
            filename="super-admin-paiements"
            title="Paiements"
            subtitle="Données actuellement affichées"
          />
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            disabled={abonnements.length === 0}
            title={abonnements.length === 0 ? "Créez d'abord un abonnement." : undefined}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
          >
            <i className="ri-add-line text-base" aria-hidden="true" />
            Nouveau paiement
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-hand-coin-line" label="Paiements" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-money-cny-circle-line" label="Encaissé (validé)" value={formatFcfa(stats.montantValide)} tone="secondary" />
        <StatCard icon="ri-hourglass-line" label="En attente" value={String(stats.attente)} tone="accent" />
        <StatCard icon="ri-close-circle-line" label="Refusés" value={String(stats.refuse)} tone="neutral" />
      </div>

      {paiements.length > 0 && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="pay-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="pay-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Référence, compte..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="pay-f-statut" className="text-xs font-label font-medium text-foreground-700">
                Statut
              </label>
              <Select
                id="pay-f-statut"
                value={statutFilter}
                onChange={(event) => setStatutFilter(event.target.value)}
                options={[{ value: "", label: "Tous les statuts" }, ...PAIEMENT_STATUT_OPTIONS]}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="pay-f-methode" className="text-xs font-label font-medium text-foreground-700">
                Méthode
              </label>
              <Select
                id="pay-f-methode"
                value={methodeFilter}
                onChange={(event) => setMethodeFilter(event.target.value)}
                options={[{ value: "", label: "Toutes les méthodes" }, ...PAIEMENT_METHODE_OPTIONS]}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="pay-f-formule" className="text-xs font-label font-medium text-foreground-700">
                Formule
              </label>
              <Select
                id="pay-f-formule"
                value={formuleFilter}
                onChange={(event) => setFormuleFilter(event.target.value)}
                options={[{ value: "", label: "Toutes les formules" }, ...formules.map((f) => ({ value: f.id, label: f.nom }))]}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="pay-sort" className="text-xs font-label font-medium text-foreground-700">
                Trier par
              </label>
              <Select
                id="pay-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                options={sortOptions}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} paiement{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {paiements.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {chargement && paiements.length === 0 && (
        <div className="flex items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-14">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-background-200 border-t-primary-500" />
        </div>
      )}

      {!chargement && erreur && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50/60 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-label font-medium text-primary-800">
            <i className="ri-error-warning-line text-base" aria-hidden="true" />
            {erreur}
          </p>
          <button
            type="button"
            onClick={() => void recharger()}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            Réessayer
          </button>
        </div>
      )}

      {!chargement && !erreur && paiements.length === 0 && (
        <EmptyState
          icon="ri-hand-coin-line"
          title="Aucun paiement"
          description={
            abonnements.length === 0
              ? "Créez d'abord un abonnement : les paiements y seront ensuite rattachés."
              : "Enregistrez le premier paiement en le rattachant à un abonnement existant."
          }
          action={
            abonnements.length > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-add-line text-base" aria-hidden="true" />
                Nouveau paiement
              </button>
            ) : undefined
          }
        />
      )}

      {paiements.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun paiement ne correspond à votre recherche ou aux filtres sélectionnés."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(paiement) => paiement.id}
          minWidthClass="min-w-[1520px]"
          caption="Paiements"
        />
      )}

      <PaiementForm open={formOpen} onClose={() => setFormOpen(false)} paiement={editing} />

      <ConfirmDeleteModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        description={
          pendingDelete
            ? `Supprimer le paiement « ${pendingDelete.reference || paiementReference(pendingDelete.id)} » ? Cette action est définitive.`
            : ""
        }
      />

      <ConfirmationModal
        open={Boolean(pendingStatut)}
        onClose={() => setPendingStatut(null)}
        onConfirm={confirmStatut}
        title={pendingStatut?.statut === "valide" ? "Valider le paiement" : "Refuser le paiement"}
        description={
          pendingStatut
            ? pendingStatut.statut === "valide"
              ? `Confirmer la validation du paiement de « ${pendingStatut.paiement.compteNom} » (${formatFcfa(pendingStatut.paiement.montant)}) ? L'abonnement sera activé.`
              : `Refuser le paiement de « ${pendingStatut.paiement.compteNom} » (${formatFcfa(pendingStatut.paiement.montant)}) ?`
            : ""
        }
        confirmLabel={pendingStatut?.statut === "valide" ? "Valider" : "Refuser"}
        icon={pendingStatut?.statut === "valide" ? "ri-checkbox-circle-line" : "ri-close-circle-line"}
        tone={pendingStatut?.statut === "valide" ? "primary" : "danger"}
      />

      <PreuvePaiementModal
        open={Boolean(preuvePaiement)}
        onClose={() => setPreuvePaiement(null)}
        path={preuvePaiement?.preuveUrl ?? ""}
        reference={preuvePaiement ? preuvePaiement.reference || paiementReference(preuvePaiement.id) : ""}
      />
    </div>
  );
}